import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { format } from "date-fns";
import React, { useContext, useEffect, useReducer, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useHistory } from "react-router-dom";
import Box from "../../../../common/box/Box";
import Content from "../../../../common/content/Content";
import Divider from "../../../../common/divider/Divider";
import { InfoBarContext } from "../../../../common/info/index";
import { Level } from "../../../../common/level";
import { MessageContext } from "../../../../common/message/index";
import { publish } from "../../../../eventBus";
import { GET, POST } from "../../../../utils/fetchUtil";
import { cleanJoin, cutShort } from "../../../../utils/stringUtil";
import { selectAllInputsQuery } from "../../submission/pdf/PdfForm";
import PdfFormErrors from "../../submission/pdf/PdfFormErrors";
import PdfFormModal from "../../submission/pdf/PdfFormModal";
import DocumentUploadButton from "../../submission/upload/DocumentUploadButton";
import DocumentUploadProgress from "../../submission/upload/DocumentUploadProgress";
import { reducer } from "./RequestSupplementReducer";

function RequestSupplementPage({ showTitle = true, isContainer = true }) {
  const history = useHistory();
  const infoBar = useContext(InfoBarContext);
  const { t } = useTranslation();
  const message = useContext(MessageContext);
  const { id, uid } = useParams();
  const [state, dispatch] = useReducer(reducer, {
    isSupplement: false,
    request: null,
    attachments: [],
    additionalDocs: [],
    supplements: [],
    supplemetDocs: [],
    letters: [],
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitEnabled, setSubmitEnabled] = useState(false);

  let letterSupplementIndex = 0;

  useEffect(() => {
    GET(
      `/requests/${id}/submissions/${uid}/supplement`,
      (request) => {
        dispatch({ type: "init", payload: request });
      },
      (error) => {
        message.toggle({
          title: t("requests.overview.supplement.infoBar.errorTitle"),
          content: error,
          hideNo: true,
          yesLabel: t("modalMessage.okLabel"),
          onYes: () => {
            message.toggle({});
          },
        });
      }
    );
  }, [id, uid]);

  useEffect(() => {
    if (state.isSupplement) {
      let invalid = [
        state.request.color,
        ...state.attachments.map((a) => a.color),
        ...state.additionalDocs.map((a) => a.color),
      ].filter((color) => color !== "green");
      setSubmitEnabled(invalid.length === 0);
    }
  }, [state]);

  const onPdfFormModalClose = () => {
    message.toggle({
      title: t("requests.overview.supplement.pdfModal.close.title"),
      content: t("requests.overview.supplement.pdfModal.close.content"),
      onYes: () => {
        message.toggle({});
        Object.keys(state.pdfForm.fieldDefinitions).forEach((id) => {
          selectAllInputsQuery(document).forEach((e) =>
            e.classList.remove("has-border-red-1-important")
          );
        });
        setIsModalOpen(false);
        dispatch({ type: "reset" });
      },
      onNo: () => message.toggle({}),
    });
  };

  const onPdfFormModalAccept = () => {
    publish({
      type: "validate-pdf-form",
      payload: {},
      onReturn: (validationErrors) => {
        if (validationErrors.length === 0) {
          setIsModalOpen(false);
          state.request.color =
            Object.keys(state.pdfForm.validationErrors).length > 0 ? "red" : "green";
          dispatch({ type: "update-documents" });
        } else {
          if (!infoBar.isOpen) {
            infoBar.toggle(t("requests.submission.pdf.pdfFormErrorInfo.errorMessageTitle"), () => (
              <PdfFormErrors errors={JSON.stringify(validationErrors)} />
            ));
          }
        }
      },
    });
  };

  const showError = (error) => {
    infoBar.toggle(t("requests.overview.supplement.infoBar.errorTitle"), () => (
      <span>{error}</span>
    ));
  };

  return (
    <Content isContainer={isContainer}>
      {state.request && (
        <div className="section">
          {state.request.supplementRequired && (
            <PdfFormModal
              title={t("requests.overview.supplement.pdfModal.title")}
              isOpen={isModalOpen}
              state={state}
              dispatch={dispatch}
              onClose={onPdfFormModalClose}
              onAccept={onPdfFormModalAccept}
            />
          )}
          {showTitle && (
            <div className="title has-text-centered p-4">
              {state.request.title}{" "}
              {state.isSupplement ? t("requests.overview.supplement.postfix") : ""}
            </div>
          )}
          <p className="is-size-6">{cutShort(state.request.info, 200, "...")}</p>
          <div className="is-size-6">
            {t("requests.overview.supplement.subject")}: {state.request.subject}
          </div>
          <div className="is-size-6">
            {t("requests.overview.supplement.submissionDate")}:{" "}
            {format(new Date(state.request.submissionDate), "dd.MM.yyyy HH:mm")}
          </div>
          {state.supplementDocs.length > 0 &&
            state.supplementDocs.map((supplement) => {
              return (
                <div>
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={supplement.href}
                    className="button mt-3 is-danger is-outlined"
                  >
                    {t("requests.overview.supplement.supplements.preview")}
                  </a>
                </div>
              );
            })}
          <div className="block">
            <Divider label={t("requests.overview.supplement.request.divider")} />
            <Box
              statusIndicatorPosition="left"
              statusIndicatorColor={state.request.color}
              className="is-size-5"
            >
              <Level className="">
                <Level.Side align="left is-flex-shrink-1">{state.pdfForm.name}</Level.Side>
                <Level.Side align="right">
                  <Level>
                    <Level.Side align="left">
                      {" "}
                      {state.request.supplementRequired && (
                        <>
                          <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            className="has-text-danger mr-4 is-clickable"
                            onClick={() => showError(state.request.error)}
                          />
                        </>
                      )}
                    </Level.Side>
                    <Level.Side align="right" className="is-flex">
                      {state.request.supplementRequired && (
                        <a
                          target="_blank"
                          rel="noopener noreferrer"
                          className="button is-pulled-left mr-2"
                          onClick={() => {
                            setIsModalOpen(true);
                            dispatch({
                              type: "save-previous-field-values",
                            });
                          }}
                        >
                          {t("requests.overview.supplement.request.supplement")}
                        </a>
                      )}
                      <a
                        target="_blank"
                        rel="noopener noreferrer"
                        className="button is-pulled-left"
                        href={state.request.href}
                      >
                        {t("requests.overview.supplement.request.preview")}
                      </a>
                    </Level.Side>
                  </Level>
                </Level.Side>
              </Level>
            </Box>
          </div>

          {
            //SUPPLEMENTS
          }
          {state.supplements.length > 0 && (
            <div className="block">
              <Divider label={t("requests.overview.supplement.supplements.divider")} />
              {state.supplements.map((supplement, index) => (
                <Box
                  key={"detail" + index}
                  statusIndicatorPosition="left"
                  statusIndicatorColor={supplement.color}
                  className="is-size-5"
                >
                  <Level>
                    <Level.Side align="left is-flex-shrink-1">
                      {index + 1}. {supplement.title}
                    </Level.Side>
                    <Level.Side align="right">
                      <Level>
                        <Level.Side align="left is-size-7">
                          {supplement.supplementRequired && (
                            <>
                              <FontAwesomeIcon
                                icon={faExclamationTriangle}
                                className="has-text-danger mr-4 is-clickable is-size-5"
                                onClick={() => showError(supplement.error)}
                              />
                              <DocumentUploadProgress
                                doc={supplement.supplement}
                                dispatchUpdateAction={() =>
                                  dispatch({
                                    type: "update-documents",
                                  })
                                }
                              />
                            </>
                          )}
                        </Level.Side>
                        <Level.Side align="right ml-2" className="is-flex">
                          {supplement.supplementRequired && (
                            <span className="mr-2">
                              <DocumentUploadButton
                                dispatchUpdateAction={() =>
                                  dispatch({
                                    type: "update-documents",
                                  })
                                }
                                doc={supplement.supplement}
                                maxDocumentSizeInMb={5}
                                formDataAsJson={{ id: uid }}
                              />
                            </span>
                          )}
                          <a
                            target="_blank"
                            rel="noopener noreferrer"
                            href={supplement.href}
                            className="button is-pulled-left"
                          >
                            {t("requests.overview.supplement.attachments.preview")}
                          </a>
                        </Level.Side>
                      </Level>
                    </Level.Side>
                  </Level>
                </Box>
              ))}
            </div>
          )}

          {
            //LETTERS
          }
          {state.letters.length > 0 && (
            <>
              <div className="block">
                <Divider label={t("requests.overview.letter.request.divider")} />
                {state.letters.map((letter, index) => (
                  <Box
                    key={"detail" + index}
                    statusIndicatorPosition="left"
                    statusIndicatorColor={letter.color}
                    className="is-size-5"
                  >
                    <Level>
                      <Level.Side align="left is-flex-shrink-1">
                        {index + 1}. {letter.title} -
                        {format(new Date(letter.submissionDate), "dd.MM.yyyy")}
                      </Level.Side>
                      <Level.Side align="right">
                        <Level>
                          <Level.Side align="right ml-2" className="is-flex">
                            <a
                              target="_blank"
                              rel="noopener noreferrer"
                              href={letter.href}
                              className="button is-pulled-left"
                            >
                              {t("requests.overview.supplement.attachments.preview")}
                            </a>
                          </Level.Side>
                        </Level>
                      </Level.Side>
                    </Level>
                  </Box>
                ))}
              </div>
              <Divider label={t("requests.overview.letter.additionalDocs.dividerSupplementPage")} />

              {state.letters.map((letter) =>
                letter.additionalDocs.map((additionalDoc, index) => (
                  <Box
                    key={"detail" + index}
                    statusIndicatorPosition="left"
                    statusIndicatorColor={
                      additionalDoc.color === null ? "warning" : additionalDoc.color
                    }
                    className="is-size-5"
                  >
                    <Level>
                      <Level.Side align="left">
                        {(letterSupplementIndex += 1)}. {cutShort(additionalDoc.label, 130, "...")}
                      </Level.Side>
                      <Level.Side align="right">
                        <Level>
                          <Level.Side align="right ml-2" className="is-flex">
                            <a
                              target="_blank"
                              rel="noopener noreferrer"
                              href={additionalDoc.href}
                              className="button is-pulled-left"
                            >
                              {t("requests.overview.supplement.attachments.preview")}
                            </a>
                          </Level.Side>
                        </Level>
                      </Level.Side>
                    </Level>
                  </Box>
                ))
              )}
            </>
          )}

          {
            <div className="block">
              <Divider label={t("requests.overview.supplement.attachments.divider")} />
              {state.attachments.map((attachment, index) => (
                <Box
                  key={"detail" + index}
                  statusIndicatorPosition="left"
                  statusIndicatorColor={attachment.color !== "grey" ? attachment.color : "red"}
                  className="is-size-5"
                >
                  <Level>
                    <Level.Side align="left is-flex-shrink-1">
                      {index + 1}. {attachment.label}
                    </Level.Side>
                    <Level.Side align="right">
                      <Level>
                        <Level.Side align="left is-size-7">
                          {attachment.supplementRequired && (
                            <>
                              <FontAwesomeIcon
                                icon={faExclamationTriangle}
                                className="has-text-danger mr-4 is-clickable is-size-5"
                                onClick={() => showError(attachment.error)}
                              />
                              <DocumentUploadProgress
                                doc={attachment.supplement}
                                dispatchUpdateAction={() =>
                                  dispatch({
                                    type: "update-documents",
                                  })
                                }
                              />
                            </>
                          )}
                        </Level.Side>
                        <Level.Side align="right ml-2" className="is-flex">
                          {attachment.supplementRequired && (
                            <span className="mr-2">
                              <DocumentUploadButton
                                dispatchUpdateAction={() =>
                                  dispatch({
                                    type: "update-documents",
                                  })
                                }
                                doc={attachment.supplement}
                                maxDocumentSizeInMb={5}
                                formDataAsJson={{ id: uid }}
                              />
                            </span>
                          )}
                          <a
                            target="_blank"
                            rel="noopener noreferrer"
                            href={attachment.href}
                            className="button is-pulled-left"
                          >
                            {t("requests.overview.supplement.attachments.preview")}
                          </a>
                        </Level.Side>
                      </Level>
                    </Level.Side>
                  </Level>
                </Box>
              ))}
            </div>
          }
          <div className="block">
            {state.additionalDocs.length > 0 && (
              <>
                <Divider label={t("requests.overview.supplement.additionalDocs.divider")} />
                {state.additionalDocs.map((additionalDoc, index) => (
                  <Box
                    key={"detail" + index}
                    statusIndicatorPosition="left"
                    statusIndicatorColor={additionalDoc.color}
                    className="is-size-5"
                  >
                    <Level>
                      <Level.Side align="left is-flex-shrink-1">
                        {index + 1}. {additionalDoc.label}
                      </Level.Side>
                      <Level.Side align="right">
                        <Level>
                          <Level.Side align="left is-size-7">
                            {additionalDoc.supplementRequired && (
                              <>
                                <DocumentUploadButton
                                  dispatchUpdateAction={() =>
                                    dispatch({
                                      type: "update-documents",
                                    })
                                  }
                                  doc={additionalDoc.supplement}
                                  maxDocumentSizeInMb={5}
                                  formDataAsJson={{ id: uid }}
                                />
                              </>
                            )}
                          </Level.Side>
                          <Level.Side align="right is-size-7">
                            {additionalDoc.supplementRequired && (
                              <DocumentUploadProgress
                                doc={additionalDoc.supplement}
                                dispatchUpdateAction={() =>
                                  dispatch({
                                    type: "update-documents",
                                  })
                                }
                              />
                            )}
                          </Level.Side>
                        </Level>
                      </Level.Side>
                    </Level>
                  </Box>
                ))}
              </>
            )}
            {state.isSupplement && (
              <Content className="has-text-centered">
                <button
                  disabled={!submitEnabled}
                  className={cleanJoin(
                    "button",
                    "pdf-form-action-button",
                    "no-focus-outline",
                    submitEnabled
                      ? "has-background-green-on-hover has-text-white-on-hover has-opacity-71-on-hover has-border-green-1"
                      : ""
                  )}
                  onClick={() => {
                    message.toggle({
                      title: t("requests.overview.supplement.submitted.title"),
                      content: t("requests.overview.supplement.submitted.content"),
                      onYes: () => {
                        message.toggle({});
                        message.toggle({ title: "", content: ("requests.overview.submitted.wait.label"), hideNo: true, hideYes: true, hideX: true, isWait: true });
                        POST(
                          `${process.env.REACT_APP_GATEWAY_URL}/requests/${state.request.id}/submissions/${state.request.uid}/supplement`,
                          state,
                          () => {
                            message.toggle({});
                            message.toggle({
                              title: t("modalMessage.titleInfo"),
                              content: t("requests.overview.supplement.submitted.okMessage"),
                              hideNo: true,
                              yesLabel: t("modalMessage.okLabel"),
                              onYes: () => {
                                message.toggle({});
                                // history.push(path, state) -> used on MyRequestsPage to scroll to Submitted
                                history.push("/requests", "/requests/#t1");
                              },
                            });
                          },
                          (error) => {
                            message.toggle({
                              title: t("requests.overview.supplement.infoBar.errorTitle"),
                              content: error,
                              hideNo: true,
                              yesLabel: t("modalMessage.okLabel"),
                              onYes: () => {
                                message.toggle({});
                              },
                            });
                          }
                        );
                      },
                      onNo: () => message.toggle({}),
                    });
                  }}
                >
                  {t("requests.overview.supplement.submit")}
                </button>
              </Content>
            )}
          </div>
        </div>
      )}
    </Content>
  );
}

export default RequestSupplementPage;
